import { assert } from '@deck.gl/core';
import { fix180Longitude, TimeDistanceMode } from '@fe/core';
import * as turf from '@turf/turf';
import { Position } from 'geojson';
import { ExpressionFilterSpecification, FilterSpecification, GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import { LngLatArray } from '../flights/types';
import { findNextPoint } from './flight-plan-flown-segment-finder';
import { DEFAULT_ROUTE_WIDTH, type FeatureIds, type FlightRoutes } from './routes-layer';
import { FlightPlanRouteWaypoint, FlightPlanSettings, RouteFlownPoint } from './types';

export const SOURCE = 'fe-routes-planned-atc-source';
export const LINE = 'fe-route-planned-atc-line';
export const LABELS = 'fe-route-planned-atc-labels';
export const POINTS = 'fe-route-planned-atc-line-pts';
export const DISTANCES = 'fe-route-planned-atc-distance-pts';

export type RouteType = 'a' | 'p' | 'd';
type FlightPlanRoutes = {
    atc: FlightPlanRouteInfo;
    planned: FlightPlanRouteInfo;
    destination: { name: string; position: LngLatArray } | null;
};

interface FlightPlanRoutePoint extends FlightPlanRouteWaypoint {
    distance: number;
}
export interface FlightInfo {
    position: Position;
    speed: number | null;
}

export interface FlightPlanRouteInfo {
    points: FlightPlanRoutePoint[];
    nextNdx: number | null;
}

enum PointStatus {
    passed = 0,
    next = 1,
    later = 2
}

interface PointFeatureProperty {
    type: RouteType;
    distanceToNext: string | null;
    timeToNext: string | null;
    status: PointStatus;
    tag: string;
}

export class FlightPlanRoutesHelper {
    private map: MaplibreMap | null = null;
    private readonly featureIds = new Map<number, FeatureIds>();
    private readonly routeInfos: Map<number, FlightPlanRoutes> = new Map();
    private _timeDistanceMode: TimeDistanceMode = TimeDistanceMode.all;
    private _settings: FlightPlanSettings | null = null;

    constructor(
        featureIds: Map<number, FeatureIds>,
        private readonly beforeId?: string
    ) {
        this.featureIds = featureIds;
    }

    get settings(): FlightPlanSettings | null {
        return this._settings;
    }

    set settings(value: FlightPlanSettings | null) {
        this._settings = value;
        this.showHide();
    }

    get timeDistanceMode(): TimeDistanceMode {
        return this._timeDistanceMode;
    }

    set timeDistanceMode(value: TimeDistanceMode) {
        this._timeDistanceMode = value;
        const filter = this._timeDistanceMode === TimeDistanceMode.nextLast ? ['==', ['get', 'status'], 1] : ['>=', ['get', 'status'], 1];
        this.map?.setFilter(DISTANCES, filter as FilterSpecification);
    }

    addToMap(map: MaplibreMap) {
        this.map = map;
        assert(this.map);

        this.map.addSource(SOURCE, {
            type: 'geojson',
            data: turf.featureCollection([]),
            tolerance: 0
        });

        this.map.addLayer(
            {
                id: LINE,
                type: 'line',
                source: SOURCE,
                paint: {
                    'line-width': DEFAULT_ROUTE_WIDTH,
                    'line-dasharray': ['match', ['get', 'type'], 'd', ['literal', [2, 2]], ['literal', [1]]],
                    'line-color': [
                        'match',
                        ['get', 'type'],
                        'p',
                        'rgba(0, 255, 255, 0.5)',
                        'a',
                        'rgba(0, 255, 0, 0.5)',
                        'd',
                        'rgba(0, 255, 0, 0.5)',
                        'transparent'
                    ]
                }
            },
            this.beforeId
        );
        this.map.addLayer(
            {
                id: POINTS,
                type: 'symbol',
                source: SOURCE,
                layout: {
                    'icon-image': 'fe:triangle-filled',
                    'icon-allow-overlap': true,
                    'icon-size': 0.3,
                    'symbol-placement': 'point'
                },
                paint: {
                    'icon-opacity': 0.2,
                    'icon-color': ['global-state', 'route-text-color']
                }
            },
            this.beforeId
        );
        this.map.addLayer(
            {
                id: LABELS,
                type: 'symbol',
                source: SOURCE,
                layout: {
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10,
                    'text-field': ['get', 'tag'],
                    'text-anchor': 'top-left',
                    'text-justify': 'left',
                    'icon-allow-overlap': true,
                    'text-allow-overlap': true,
                    'text-ignore-placement': true,
                    'text-optional': false,
                    'text-offset': [0.0, 0.7]
                },
                paint: {
                    'text-color': ['global-state', 'route-text-color']
                }
            },
            this.beforeId
        );
        this.map.addLayer(
            {
                id: DISTANCES,
                type: 'symbol',
                source: SOURCE,
                layout: {
                    'text-font': ['Open Sans Regular'],
                    'text-anchor': 'top-left',
                    'text-size': 10,
                    'text-field': ['concat', ['get', 'distanceToNext'], ' NM / ', ['get', 'timeToNext'], ' min'],
                    'icon-allow-overlap': true,
                    'text-justify': 'left',
                    'text-optional': false,
                    'text-allow-overlap': false,
                    'text-ignore-placement': false,
                    'text-offset': [
                        'case',
                        ['any', ['==', ['get', 'tag'], null], ['==', ['get', 'tag'], '']],
                        ['literal', [0, 0.7]],
                        ['literal', [0, 2.1]]
                    ],
                    'text-max-width': 999
                },
                paint: {
                    'text-color': ['global-state', 'route-text-color']
                },
                filter: [
                    'all',
                    ['==', ['geometry-type'], 'Point'],
                    this._timeDistanceMode === TimeDistanceMode.nextLast ? ['==', ['get', 'status'], 1] : ['>=', ['get', 'status'], 1]
                ]
            },
            this.beforeId
        );

        this.showHide();
    }

    removeFromMap() {
        assert(this.map);

        this.map.removeLayer(LINE);
        this.map.removeLayer(POINTS);
        this.map.removeLayer(LABELS);
        this.map.removeLayer(DISTANCES);
        this.map.removeSource(SOURCE);
        this.routeInfos.clear();
        this.map = null;
    }

    remove(id: number) {
        assert(this.map);

        const source = this.map.getSource(SOURCE) as GeoJSONSource;
        source.updateData({
            remove: this.featureIds.get(id)!.flightPlan
        });

        this.routeInfos.delete(id);
    }

    removeAll() {
        const source = this.map?.getSource(SOURCE) as GeoJSONSource;
        source?.updateData({
            removeAll: true
        });

        this.routeInfos.clear();
    }

    upsert(id: number, routes: FlightRoutes, flight: FlightInfo | null): GeoJSON.Feature[] {
        const source = this.map!.getSource(SOURCE) as GeoJSONSource;
        assert(source);

        const routeInfos: FlightPlanRoutes = {
            atc: {
                points: this.calculateDistances(routes.atc?.waypoints),
                nextNdx: null
            },
            planned: {
                points: this.calculateDistances(routes.planned?.waypoints),
                nextNdx: null
            },
            destination: routes.destination
        };

        routeInfos.atc.nextNdx = findNextPoint(routeInfos.atc, flight?.position ?? null);
        routeInfos.planned.nextNdx = findNextPoint(routeInfos.planned, flight?.position ?? null);
        this.routeInfos.set(id, routeInfos);

        const features = this.toFeatures(id, routeInfos, flight, routes);
        source.updateData({
            add: features,
            remove: this.featureIds.get(id)?.flightPlan
        });

        return features;
    }

    lastFlownPointChanged(flightId: number, point: RouteFlownPoint) {
        // TODO: this should also update periodically not only when aircraft position changes (60s maybe?)

        const routeInfos = this.routeInfos.get(flightId);
        if (!routeInfos) {
            return;
        }

        const source = this.map!.getSource(SOURCE) as GeoJSONSource;
        assert(source);

        routeInfos.atc.nextNdx = findNextPoint(routeInfos.atc, point?.position ?? null);
        routeInfos.planned.nextNdx = findNextPoint(routeInfos.planned, point?.position ?? null);

        const planned = this.toPointFeatures(flightId, routeInfos.planned, point, 'p');
        const atc = this.toPointFeatures(flightId, routeInfos.atc, point, 'a');
        const features = [...planned, ...atc];

        if (this.settings?.showTempPlanned && routeInfos.destination) {
            if (routeInfos.atc.points.length === 0 && routeInfos.planned.points.length === 0) {
                const newLine = toGreatCircleSegments(
                    flightId,
                    [{ position: point.position }, { position: routeInfos.destination.position }],
                    'd'
                );
                source.updateData({
                    update: [
                        {
                            id: newLine.id!,
                            newGeometry: newLine.geometry,
                            addOrUpdateProperties: Object.entries(newLine.properties!).map(([key, value]) => ({ key, value }))
                        }
                    ]
                });
            }
        }

        if (features.length > 0) {
            source.updateData({
                update: features.map((x) => ({
                    id: x.id!,
                    addOrUpdateProperties: Object.entries(x.properties).map(([key, value]) => ({ key, value }))
                }))
            });
        }
    }

    private showHide() {
        if (!this.map || !this.settings) {
            return;
        }

        const { show, showPlanned, showAtc, showPoints, showLabels, showDistanceLabels } = this.settings;

        const typeFilter: ExpressionFilterSpecification = ['any', ['==', ['get', 'type'], 'd']];
        if (showPlanned) {
            typeFilter.push(['==', ['get', 'type'], 'p']);
        }

        if (showAtc) {
            typeFilter.push(['==', ['get', 'type'], 'a']);
        }

        this.map.setFilter(LINE, ['all', ['==', ['geometry-type'], 'LineString'], typeFilter]);
        this.map.setFilter(POINTS, ['all', ['==', ['geometry-type'], 'Point'], typeFilter]);
        this.map.setFilter(LABELS, ['all', ['==', ['geometry-type'], 'Point'], typeFilter]);

        const distanceFilter: ExpressionFilterSpecification =
            this._timeDistanceMode === TimeDistanceMode.nextLast ? ['==', ['get', 'status'], 1] : ['>=', ['get', 'status'], 1];
        this.map.setFilter(DISTANCES, ['all', ['==', ['geometry-type'], 'Point'], distanceFilter, typeFilter]);

        this.map.setLayoutProperty(LINE, 'visibility', show ? 'visible' : 'none');
        this.map.setLayoutProperty(POINTS, 'visibility', show && showPoints ? 'visible' : 'none');
        this.map.setLayoutProperty(LABELS, 'visibility', show && showPoints && showLabels ? 'visible' : 'none');
        this.map.setLayoutProperty(DISTANCES, 'visibility', show && showDistanceLabels ? 'visible' : 'none');
    }

    private calculateDistances(waypoints: FlightPlanRouteWaypoint[] | undefined): FlightPlanRoutePoint[] {
        if (!waypoints) {
            return [];
        }

        const results: FlightPlanRoutePoint[] = [];
        for (let i = 0; i < waypoints.length; i++) {
            const wp = waypoints[i];
            const d = i > 0 ? turf.distance(waypoints[i - 1].position, wp.position, { units: 'nauticalmiles' }) : 0;
            results.push({ ...wp, distance: d });
        }
        return results;
    }

    private toPointFeatures(
        id: number,
        routeInfo: FlightPlanRouteInfo,
        flight: FlightInfo | null,
        type: RouteType
    ): GeoJSON.Feature<GeoJSON.Point, PointFeatureProperty>[] {
        if (!routeInfo) {
            return [];
        }

        const { points, nextNdx } = routeInfo;
        let distanceToNext =
            flight && nextNdx != null && points[nextNdx]
                ? turf.distance(flight.position, points[nextNdx].position, { units: 'nauticalmiles' })
                : null;
        const { speed = 0 } = flight || {};
        let timeToNext = distanceToNext != null && speed! > 0 ? (distanceToNext / speed!) * 60 : null;

        return points.map((x, i) => {
            if (nextNdx != null && i > nextNdx) {
                if (distanceToNext != null) {
                    distanceToNext += x.distance;
                    if (timeToNext != null) {
                        timeToNext += (x.distance / speed!) * 60;
                    }
                }
            }

            return turf.point(
                x.position,
                {
                    tag: x.name,
                    distanceToNext: distanceToNext?.toFixed(1) ?? null,
                    timeToNext: timeToNext?.toFixed(1) ?? null,
                    type,
                    status:
                        nextNdx == null || i < nextNdx
                            ? PointStatus.passed
                            : i === nextNdx || i === points.length - 1
                              ? PointStatus.next
                              : PointStatus.later
                },
                {
                    id: `${id}-${type}-${i}-p`
                }
            );
        });
    }

    private toFeatures(flightId: number, routeInfos: FlightPlanRoutes, flight: FlightInfo | null, routes: FlightRoutes): GeoJSON.Feature[] {
        const features = [] as GeoJSON.Feature[];
        if (routes.planned) {
            features.push(...(this.toPointFeatures(flightId, routeInfos.planned, flight, 'p') as GeoJSON.Feature[]));
            features.push(toGreatCircleSegments(flightId, routeInfos.planned.points!, 'p'));
        }

        if (routes.atc) {
            features.push(...this.toPointFeatures(flightId, routeInfos.atc, flight, 'a'));
            features.push(toGreatCircleSegments(flightId, routeInfos.atc.points!, 'a'));
        }

        if (this._settings?.showTempPlanned) {
            if (!routes.atc && !routes.planned && routes.destination) {
                const last = routes.flown?.at(-1);
                if (last) {
                    features.push(
                        toGreatCircleSegments(flightId, [{ position: last.position }, { position: routes.destination.position }], 'd')
                    );
                }
            }
        }

        return features;
    }
}

function toGreatCircleSegments(flightId: number, waypoints: Array<{ position: LngLatArray }>, type: RouteType): GeoJSON.Feature {
    const segments: GeoJSON.Position[][] = [];

    for (let i = 0; i < waypoints.length - 1; i++) {
        const p1 = waypoints[i].position;
        const p2 = waypoints[i + 1].position;
        const distance = turf.distance(p1, p2, { units: 'nauticalmiles' });

        // If less < 50 skip great circle, otherwise use 50 miles segments (fe-desktop does 1 mile segments but that's too heavy for maplibre)
        const feature =
            distance < 50
                ? turf.lineString([p1, p2])
                : turf.greatCircle(p1, p2, {
                      properties: {
                          type: type
                      },
                      npoints: Math.ceil(distance / 50)
                  });

        if (feature.geometry.type === 'LineString') {
            fixSegment180(feature.geometry.coordinates);
            segments.push(feature.geometry.coordinates);
        } else {
            segments.push(...feature.geometry.coordinates);
        }
    }
    return turf.multiLineString(
        segments,
        {
            type: type
        },
        {
            id: `${flightId}-${type}-l`
        }
    );
}

function fixSegment180(segments: Position[] | null) {
    // Apply correction when segment lng changes from -180 to 180 or from 180 to -180
    if (segments && segments.length > 1 && Math.abs(segments[0][0] - segments[segments.length - 1][0]) > 180) {
        for (let i = 0; i < segments.length - 1; i++) {
            const fixedLng = fix180Longitude(segments[i][0], segments[i + 1][0]);
            segments[i + 1] = [fixedLng, segments[i + 1][1]];
        }
    }
}
